import type { CarNotificationDefaultRecord, OrganizationCarDriverRecord } from '@navio/nsdk';
import { getUserDisplayName } from 'noddi-util';

export type OrganizationBookingPerson = {
  userId: number;
  name: string;
};

type OrganizationBookingChoice = OrganizationBookingPerson & {
  kind: 'driver' | 'you';
};

/** Null when there is nothing to choose: the booking stays on the booker. */
export type OrganizationBookingAssignee = {
  defaultUserId: number;
  choices: OrganizationBookingChoice[];
};

/**
 * Who an organization booking is assigned to.
 * No other driver means the booker, with no choice. Otherwise the default is
 * the first other driver by name, then user id, and the booker can choose
 * themselves. A booker who is already a driver is listed once, as a driver.
 */
export function organizationBookingAssignee(args: {
  booker: OrganizationBookingPerson;
  drivers: OrganizationBookingPerson[];
}): OrganizationBookingAssignee | null {
  const drivers = uniquePeople(args.drivers).sort(comparePeople);
  const defaultDriver = drivers.find((driver) => driver.userId !== args.booker.userId);
  if (defaultDriver == null) {
    return null;
  }
  const bookerIsDriver = drivers.some((driver) => driver.userId === args.booker.userId);
  const choices: OrganizationBookingChoice[] = drivers.map((driver) => ({ ...driver, kind: 'driver' }));
  if (!bookerIsDriver) {
    choices.push({ ...args.booker, kind: 'you' });
  }
  return { defaultUserId: defaultDriver.userId, choices };
}

/**
 * A pick of null uses the default. A pick that is no longer offered falls
 * back to that driver, for as long as one remains.
 */
export function selectedOrganizationBookingUserId(args: {
  pickedUserId: number | null;
  assignee: OrganizationBookingAssignee | null;
  bookerUserId: number | null;
}): number | null {
  if (args.assignee == null) {
    return args.bookerUserId;
  }
  const pickedIsOffered =
    args.pickedUserId != null && args.assignee.choices.some((choice) => choice.userId === args.pickedUserId);
  return pickedIsOffered ? args.pickedUserId : args.assignee.defaultUserId;
}

/**
 * The booking party already says who this booking is on.
 * A member organization uses its drivers for any payment. A non-member uses
 * the company's drivers only once the booking is on that company.
 */
export function organizationBookingDrivers(args: {
  party: { kind: 'member'; isOrganization: boolean } | { kind: 'nonMember' };
  memberDrivers: OrganizationBookingPerson[];
  nonMemberDrivers: OrganizationBookingPerson[];
}): OrganizationBookingPerson[] {
  if (args.party.kind === 'nonMember') {
    return args.nonMemberDrivers;
  }
  return args.party.isOrganization ? args.memberDrivers : [];
}

export function toOrganizationBookingPeople(
  users: ReadonlyArray<
    Pick<CarNotificationDefaultRecord | OrganizationCarDriverRecord, 'userId' | 'firstName' | 'lastName'>
  >
): OrganizationBookingPerson[] {
  return users.map((user) => ({ userId: user.userId, name: getUserDisplayName(user) }));
}

function uniquePeople(people: OrganizationBookingPerson[]): OrganizationBookingPerson[] {
  const byUserId = new Map<number, OrganizationBookingPerson>();
  for (const person of people) {
    if (!byUserId.has(person.userId)) {
      byUserId.set(person.userId, person);
    }
  }
  return [...byUserId.values()];
}

function comparePeople(left: OrganizationBookingPerson, right: OrganizationBookingPerson): number {
  const byName = left.name.localeCompare(right.name);
  if (byName !== 0) {
    return byName;
  }
  return left.userId - right.userId;
}
